/**
 * Task Detail Component
 * 
 * Expanded view of a task with full details and inline editing.
 * Click on any field to edit it inline.
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import {
  X,
  Check,
  Tag,
  Loader2,
  AlertCircle,
  Pencil,
  Plus,
  Copy,
  Archive,
  ClipboardCheck,
  ArrowUpCircle,
  RotateCcw,
  Clock,
  CheckCircle2,
  CalendarDays,
  Zap,
  Package,
} from 'lucide-react';
import { parseISO, isPast, isToday, isValid, addDays, nextSaturday, nextMonday, setHours, setMinutes, setSeconds, setMilliseconds, format } from 'date-fns';
import * as Popover from '@radix-ui/react-popover';
import clsx from 'clsx';
import type { Task } from '../types';
import { useProjects } from '../hooks/useProjects';
import { useLabels } from '../hooks/useLabels';
import { useTaskMutations } from '../hooks/mutations/useTaskMutations';
import { TaskComments } from './TaskComments';
import { SubtasksList } from './SubtasksList';
import { TaskTimerSection } from './TaskTimerSection';
import { DatePicker } from './DatePicker';
import { formatCompletedDate } from '../utils/dates';
import { MarkdownContent } from './MarkdownContent';
import { TaskMetadataGrid, FieldRow, FieldSelect, inputClasses } from './ui/taskFields';
import { btn } from './ui/buttonStyles';
import { createProject } from '../api/projects';

interface TaskDetailProps {
  task: Task;
  onClose: () => void;
  onComplete: (taskId: string) => void;
  onReopen?: (taskId: string) => void;
  onEdit?: (task: Task) => void;
  onTaskUpdate?: (updatedTask: Task) => void;
  onDuplicate?: (newTask: Task) => void;
  onDelete?: (taskId: string) => void;
  /** Callback when a subtask is selected */
  onSelectTask?: (taskId: string) => void;
  /** True if task is in pending completion state (recently completed, in undo window) */
  isPendingCompletion?: boolean;
}

const priorityColors: Record<number, string> = {
  1: 'text-priority-1 bg-red-50 dark:bg-red-950/50',
  2: 'text-priority-2 bg-orange-50 dark:bg-orange-950/50',
  3: 'text-priority-3 bg-yellow-50 dark:bg-yellow-950/50',
  4: 'text-text-secondary-light bg-surface-base-light dark:text-text-secondary-dark dark:bg-surface-raised-dark/50',
};

// Priority colors for checkbox border (matches TaskList)
const priorityBorderColors: Record<number, string> = {
  1: 'border-priority-1 hover:bg-red-50 dark:hover:bg-red-900/20',
  2: 'border-priority-2 hover:bg-orange-50 dark:hover:bg-orange-900/20',
  3: 'border-priority-3 hover:bg-yellow-50 dark:hover:bg-yellow-900/20',
  4: 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800',
};

// Priority colors for hover checkmark icon
const priorityCheckColors: Record<number, string> = {
  1: 'text-priority-1',
  2: 'text-priority-2',
  3: 'text-priority-3',
  4: 'text-gray-400 dark:text-gray-400',
};

const priorityOptions = [
  { value: 1, label: 'Urgent', color: 'text-priority-1' },
  { value: 2, label: 'High', color: 'text-priority-2' },
  { value: 3, label: 'Medium', color: 'text-priority-3' },
  { value: 4, label: 'Low', color: 'text-text-secondary-light' },
];

/**
 * Priority value the native <select> should show. An unset (null/undefined)
 * priority has no matching <option>, so the browser would display the first
 * one ("Urgent") while state stayed null — re-picking Urgent then fired no
 * change. Coalesce unset to 4 (Low, the meaning of a null priority here) so the
 * value always matches a real option and every selection persists.
 */
export function priorityValueForSelect(priority: number | null | undefined): number {
  return priority ?? 4;
}

type EditableField = 'priority' | 'dueDate' | 'description' | 'title' | 'project' | 'labels' | 'timeEstimate' | 'recurrenceRule';

// Time bucket labels that are managed by the Estimate field, not the Labels field
const TIME_ESTIMATE_LABELS = [
  '15-minutes',
  '30-minutes', 
  '45-minutes',
  '1-hour',
  '90-minutes',
  '2-hours',
  'needs-breakdown',
];

// Recurrence rule options
const RECURRENCE_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekday', label: 'Every weekday' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
];

// Time estimate options for the dropdown (user-friendly labels)
const TIME_ESTIMATE_OPTIONS = [
  { value: '15-minutes', label: '15 minutes' },
  { value: '30-minutes', label: '30 minutes' },
  { value: '45-minutes', label: '45 minutes' },
  { value: '1-hour', label: '1 hour' },
  { value: '90-minutes', label: '90 minutes' },
  { value: '2-hours', label: '2 hours' },
  { value: 'needs-breakdown', label: 'Needs breakdown' },
];

/**
 * Check if a label is a time estimate label (case-insensitive)
 */
const isTimeEstimateLabel = (label: string): boolean => {
  const normalized = label.toLowerCase().replace(/\s+/g, '-');
  return TIME_ESTIMATE_LABELS.includes(normalized);
};

/**
 * Filter out time estimate labels from an array of labels
 */
const filterTimeLabels = (labels: string[]): string[] => {
  return labels.filter(l => !isTimeEstimateLabel(l));
};

export function TaskDetail({ task, onClose, onComplete, onReopen, onEdit: _onEdit, onTaskUpdate, onDuplicate, onDelete, onSelectTask, isPendingCompletion = false }: TaskDetailProps) {
  const { projects, refetch: refetchProjects } = useProjects();
  const { labelNames: existingLabels } = useLabels();
  const {
    completeTaskAsync,
    updateTaskAsync,
    duplicateTaskAsync,
    deleteTaskAsync,
  } = useTaskMutations();
  
  const [isCompleting, setIsCompleting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [completionMinutes, setCompletionMinutes] = useState<string>('');
  const [showTimeInput, setShowTimeInput] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  
  const [hasCopied, setHasCopied] = useState(false);
  
  // Inline editing state
  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [editPriority, setEditPriority] = useState(task.priority);
  const [editDueDate, setEditDueDate] = useState(task.due?.date || '');
  const [editDueTime, setEditDueTime] = useState(() => {
    // Extract HH:MM from existing datetime if present
    if (task.due?.datetime) {
      const m = task.due.datetime.match(/T(\d{2}:\d{2})/);
      return m ? m[1] : '';
    }
    return '';
  });
  const [editDescription, setEditDescription] = useState(task.description || '');
  const [editTitle, setEditTitle] = useState(task.content);
  const [editProject, setEditProject] = useState(task.projectId || '');
  const [editTimeEstimate, setEditTimeEstimate] = useState(task.metadata?.timeEstimate || '');
  const [editRecurrenceRule, setEditRecurrenceRule] = useState((task as any).recurrenceRule ?? '');
  const [savingField, setSavingField] = useState<EditableField | null>(null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  
  // Labels editing state (more complex - array-based with suggestions)
  const [selectedLabels, setSelectedLabels] = useState<string[]>(task.labels || []);
  const [labelInput, setLabelInput] = useState('');
  const [showLabelSuggestions, setShowLabelSuggestions] = useState(false);
  const [highlightedSuggestion, setHighlightedSuggestion] = useState(0);

  // Snooze state
  const [showSnoozePopover, setShowSnoozePopover] = useState(false);
  const [isSnoozing, setIsSnoozing] = useState(false);
  const [snoozeError, setSnoozeError] = useState<string | null>(null);
  const [snoozePickerDate, setSnoozePickerDate] = useState('');
  const [showSnoozeDatePicker, setShowSnoozeDatePicker] = useState(false);

  // Get current time estimate from labels
  const currentTimeEstimate = useMemo(() => {
    return (task.labels || []).find((l: string) => isTimeEstimateLabel(l)) || '';
  }, [task.labels]);
  
  // Refs for focus management
  const priorityRef = useRef<HTMLSelectElement>(null);
  // dueDateRef intentionally removed — DatePicker does not expose a ref target;
  // the popover opens via click and doesn't need programmatic focus
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const descriptionPreviewRef = useRef<HTMLDivElement>(null);
  // Persists the preview div height across re-renders so we can seed the textarea
  // height when transitioning to edit mode (preview div is unmounted at that point).
  const descriptionPreviewHeightRef = useRef<number>(0);
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const projectRef = useRef<HTMLSelectElement>(null);
  const labelsRef = useRef<HTMLInputElement>(null);
  const timeEstimateRef = useRef<HTMLSelectElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  /** Build a UTC ISO string for a given local date at 9am */
  const localDateAt9am = (date: Date): string => {
    const d = setMilliseconds(setSeconds(setMinutes(setHours(date, 9), 0), 0), 0);
    return d.toISOString();
  };

  const SNOOZE_OPTIONS = [
    { label: 'Tomorrow', getValue: () => localDateAt9am(addDays(new Date(), 1)) },
    { label: 'This Weekend', getValue: () => localDateAt9am(nextSaturday(new Date())) },
    { label: 'Next Week', getValue: () => localDateAt9am(nextMonday(new Date())) },
    { label: 'In 2 Weeks', getValue: () => localDateAt9am(addDays(new Date(), 14)) },
  ];

  const handleSnooze = async (snoozeUntil: string) => {
    setIsSnoozing(true);
    setSnoozeError(null);
    try {
      const updated = await updateTaskAsync({ taskId: task.id, updates: { snoozeUntil } });
      onTaskUpdate?.(updated);
      setShowSnoozePopover(false);
      setSnoozePickerDate('');
      setShowSnoozeDatePicker(false);
    } catch (err) {
      setSnoozeError(err instanceof Error ? err.message : 'Failed to snooze');
    } finally {
      setIsSnoozing(false);
    }
  };

  const handleClearSnooze = async () => {
    setIsSnoozing(true);
    setSnoozeError(null);
    try {
      const updated = await updateTaskAsync({ taskId: task.id, updates: { snoozeUntil: null } });
      onTaskUpdate?.(updated);
    } catch (err) {
      setSnoozeError(err instanceof Error ? err.message : 'Failed to clear snooze');
    } finally {
      setIsSnoozing(false);
    }
  };

  const handleSnoozeDatePick = async (dateStr: string) => {
    if (!dateStr) return;
    setSnoozePickerDate(dateStr);
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return;
    const localDate = new Date(year, month - 1, day);
    await handleSnooze(localDateAt9am(localDate));
  };

  const formatSnoozeDisplay = (snoozeUntil: string): string => {
    const d = new Date(snoozeUntil);
    return format(d, 'MMM d');
  };

  // Auto-resize description textarea based on content
  useEffect(() => {
    const textarea = descriptionRef.current;
    if (!textarea) return;
    
    // Reset height to get accurate scrollHeight
    textarea.style.height = 'auto';
    
    // Set height based on content, with max of 300px
    const maxHeight = 300;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${newHeight}px`;
  }, [editDescription, task.description]);


  // Auto-resize title textarea based on content
  useEffect(() => {
    const textarea = titleRef.current;
    if (!textarea || editingField !== 'title') return;
    
    // Reset height to get accurate scrollHeight
    textarea.style.height = 'auto';
    
    // Set height based on content (no max for title)
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [editTitle, editingField]);
  
  // Filter out time estimate labels from existing labels for suggestions
  const availableLabels = useMemo(() => {
    return existingLabels.filter(l => !isTimeEstimateLabel(l));
  }, [existingLabels]);
  
  // Filter suggestions based on input
  const labelSuggestions = useMemo(() => {
    if (!labelInput.trim()) {
      // Show all existing labels not already selected (excluding time labels)
      return availableLabels.filter(l => !selectedLabels.includes(l)).slice(0, 8);
    }
    const query = labelInput.toLowerCase().trim();
    return availableLabels
      .filter(l => l.toLowerCase().includes(query) && !selectedLabels.includes(l))
      .slice(0, 8);
  }, [labelInput, availableLabels, selectedLabels]);
  
  // Check if current input could be a new label
  const canAddNewLabel = useMemo(() => {
    const trimmed = labelInput.trim();
    if (!trimmed) return false;
    // Don't suggest adding if it matches an existing label exactly
    const exactMatch = existingLabels.some(l => l.toLowerCase() === trimmed.toLowerCase());
    // Don't add if already selected
    const alreadySelected = selectedLabels.some(l => l.toLowerCase() === trimmed.toLowerCase());
    return !exactMatch && !alreadySelected;
  }, [labelInput, existingLabels, selectedLabels]);
  
  // Sync edit state when task changes
  useEffect(() => {
    setEditPriority(task.priority);
    setEditDueDate(task.due?.date || '');
    // Extract HH:MM from datetime if present
    if (task.due?.datetime) {
      const m = task.due.datetime.match(/T(\d{2}:\d{2})/);
      setEditDueTime(m ? (m[1] ?? '') : '');
    } else {
      setEditDueTime('');
    }
    setEditDescription(task.description || '');
    setEditTitle(task.content);
    setEditProject(task.projectId || '');
    // Filter out time estimate labels - they're managed by the Estimate field
    setSelectedLabels(filterTimeLabels(task.labels || []));
    setLabelInput('');
    // Get time estimate from labels
    const timeLabel = (task.labels || []).find((l: string) => isTimeEstimateLabel(l)) || '';
    setEditTimeEstimate(timeLabel);
    setEditRecurrenceRule((task as any).recurrenceRule ?? '');
    setEditingField(null);
  }, [task]);
  
  // Focus input when editing starts
  useEffect(() => {
    const focusMap: Partial<Record<EditableField, React.RefObject<HTMLElement | null>>> = {
      priority: priorityRef,
      // dueDate: no ref — DatePicker opens via click, not programmatic focus
      description: descriptionRef,
      title: titleRef,
      project: projectRef,
      labels: labelsRef,
      timeEstimate: timeEstimateRef,
    };
    
    if (editingField && focusMap[editingField]?.current) {
      const el = focusMap[editingField].current;
      el?.focus();
      
      // Select all text for title textarea
      if (editingField === 'title' && titleRef.current) {
        titleRef.current.select();
      }
      
      // Move cursor to end for description textarea (height is seeded by useLayoutEffect above)
      if (editingField === 'description' && descriptionRef.current) {
        const len = descriptionRef.current.value.length;
        descriptionRef.current.setSelectionRange(len, len);
      }
      
      // Show suggestions for labels
      if (editingField === 'labels') {
        setShowLabelSuggestions(true);
      }
    }
  }, [editingField]);

  // Close label suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showLabelSuggestions &&
        suggestionsRef.current &&
        labelsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        !labelsRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest?.('[data-labels-container]')
      ) {
        setShowLabelSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLabelSuggestions]);

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      const minutes = completionMinutes ? parseInt(completionMinutes, 10) : undefined;
      await completeTaskAsync({ taskId: task.id, options: { actualMinutes: minutes } });
      onComplete(task.id);
    } catch (err) {
      console.error('Failed to complete task:', err);
    } finally {
      setIsCompleting(false);
    }
  };
  
  /**
   * Duplicate the current task
   */
  const handleDuplicate = async () => {
    setIsDuplicating(true);
    try {
      const newTask = await duplicateTaskAsync({ task });
      onDuplicate?.(newTask);
    } catch (err) {
      console.error('Failed to duplicate task:', err);
      setSaveError('Failed to duplicate task');
    } finally {
      setIsDuplicating(false);
    }
  };

  /**
   * Delete/archive the current task
   */
  const handleDelete = async () => {
    // Confirm before deleting
    const confirmed = window.confirm(
      `Are you sure you want to archive "${task.content}"?\n\nThis will permanently delete the task from your task list.`
    );
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await deleteTaskAsync({ taskId: task.id });
      onDelete?.(task.id);
      onClose();
    } catch (err) {
      console.error('Failed to delete task:', err);
      setSaveError('Failed to archive task');
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Copy description to clipboard
   */
  const handleCopyDescription = async () => {
    if (!task.description) return;
    
    try {
      await navigator.clipboard.writeText(task.description);
      setHasCopied(true);
      setTimeout(() => setHasCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy description:', err);
    }
  };
  
  /**
   * Save a single field inline
   */
  const saveField = async (field: EditableField, overrideValue?: string | number | string[]) => {
    // Get the current value for this field
    const getCurrentValue = (): string | number | string[] | undefined => {
      if (overrideValue !== undefined) return overrideValue;
      switch (field) {
        case 'priority': return editPriority;
        case 'dueDate': return editDueDate;
        case 'description': return editDescription;
        case 'title': return editTitle;
        case 'project': return editProject;
        case 'labels': return selectedLabels;
        case 'timeEstimate': return editTimeEstimate;
        case 'recurrenceRule': return editRecurrenceRule;
        default: return undefined;
      }
    };

    // Get the original value for comparison
    const getOriginalValue = (): string | number | string[] | undefined => {
      switch (field) {
        case 'priority': return task.priority;
        case 'dueDate': return task.due?.date || '';
        case 'description': return task.description || '';
        case 'title': return task.content;
        case 'project': return task.projectId || '';
        case 'labels': return filterTimeLabels(task.labels || []);
        case 'timeEstimate': return currentTimeEstimate;
        case 'recurrenceRule': return (task as any).recurrenceRule ?? '';
        default: return undefined;
      }
    };

    const value = getCurrentValue();
    const original = getOriginalValue();
    
    // Check if value changed
    const hasChanged = field === 'labels' 
      ? JSON.stringify(value) !== JSON.stringify(original)
      : value !== original;
    
    if (!hasChanged) {
      setEditingField(null);
      setShowLabelSuggestions(false);
      return;
    }
    
    // Validate title
    if (field === 'title' && !String(value).trim()) {
      setSaveError('Task title cannot be empty');
      return;
    }
    
    setSavingField(field);
    setSaveError(null);
    
    try {
      const updates: Record<string, unknown> = {};
      
      switch (field) {
        case 'priority':
          updates.priority = value;
          break;
        case 'dueDate':
          updates.dueDate = value || '';
          // Always include dueTime so clearing the date also clears the time,
          // and so a previously-set time is preserved/updated when changing the date.
          updates.dueTime = value ? editDueTime : '';
          break;
        case 'description':
          updates.description = value || undefined;
          break;
        case 'title':
          updates.content = String(value).trim();
          break;
        case 'project':
          updates.projectId = value || undefined;
          break;
        case 'labels': {
          // Preserve existing time estimate labels when saving custom labels
          const existingTimeLabels = (task.labels || []).filter((l: string) => isTimeEstimateLabel(l));
          const newCustomLabels = value as string[];
          updates.labels = [...newCustomLabels, ...existingTimeLabels];
          break;
        }
        case 'timeEstimate': {
          // Time estimate is stored as a label - update labels array
          const customLabels = filterTimeLabels(task.labels || []);
          const newTimeLabel = value as string;
          updates.labels = newTimeLabel
            ? [...customLabels, newTimeLabel]
            : customLabels; // Remove time label if cleared
          break;
        }
        case 'recurrenceRule':
          updates.recurrenceRule = (value as string) || null;
          break;
      }
      
      const updatedTask = await updateTaskAsync({ taskId: task.id, updates });
      setEditingField(null);
      setShowLabelSuggestions(false);
      onTaskUpdate?.(updatedTask);
    } catch (err: any) {
      console.error(`Failed to update ${field}:`, err);
      setSaveError(err.message || `Failed to update ${field}`);
    } finally {
      setSavingField(null);
    }
  };
  
  /**
   * Cancel editing and reset to original value
   */
  const cancelEdit = () => {
    setEditPriority(task.priority);
    setEditDueDate(task.due?.date || '');
    if (task.due?.datetime) {
      const m = task.due.datetime.match(/T(\d{2}:\d{2})/);
      setEditDueTime(m ? (m[1] ?? '') : '');
    } else {
      setEditDueTime('');
    }
    setEditDescription(task.description || '');
    setEditTitle(task.content);
    setEditProject(task.projectId || '');
    setSelectedLabels(filterTimeLabels(task.labels || []));
    setLabelInput('');
    // Get time estimate from labels
    const timeLabel = (task.labels || []).find((l: string) => isTimeEstimateLabel(l)) || '';
    setEditTimeEstimate(timeLabel);
    setEditRecurrenceRule((task as any).recurrenceRule ?? '');
    setEditingField(null);
    setShowLabelSuggestions(false);
    setSaveError(null);
  };
  
  const NEW_PROJECT_VALUE = '__new__';

  const handleCreateProject = async () => {
    const name = newProjectName.trim();
    if (!name || isCreatingProject) return;
    setIsCreatingProject(true);
    setSaveError(null);
    try {
      const project = await createProject({ name });
      await refetchProjects();
      setEditProject(project.id);
      void saveField('project', project.id);
      setShowNewProject(false);
      setNewProjectName('');
    } catch (err) {
      // Surface the failure — swallowing it here made a broken create endpoint
      // look like a dead button. Field stays open for retry.
      setSaveError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setIsCreatingProject(false);
    }
  };

  const handleProjectChange = (value: string) => {
    if (value === NEW_PROJECT_VALUE) {
      setShowNewProject(true);
      return;
    }
    setEditProject(value);
    void saveField('project', value);
    setShowNewProject(false);
  };

  /**
   * Handle keyboard events for inline editing
   */
  const handleKeyDown = (e: React.KeyboardEvent, field: EditableField) => {
    if (e.key === 'Escape') {
      cancelEdit();
    } else if (e.key === 'Enter' && field !== 'description' && field !== 'labels') {
      e.preventDefault();
      saveField(field);
    } else if (e.key === 'Enter' && e.metaKey && field === 'description') {
      e.preventDefault();
      saveField(field);
    }
  };
  
  /**
   * Handle label-specific keyboard events
   */
  const handleLabelKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      cancelEdit();
      return;
    }
    
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // If there's a highlighted suggestion, add it
      if (labelSuggestions.length > 0 && highlightedSuggestion < labelSuggestions.length) {
        addLabel(labelSuggestions[highlightedSuggestion]);
      } else if (canAddNewLabel) {
        // Add as new label
        addLabel(labelInput.trim());
      } else if (!labelInput.trim()) {
        // No input, save and close
        saveField('labels');
      }
      return;
    }
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const maxIndex = labelSuggestions.length + (canAddNewLabel ? 1 : 0) - 1;
      setHighlightedSuggestion(prev => Math.min(prev + 1, maxIndex));
      return;
    }
    
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedSuggestion(prev => Math.max(prev - 1, 0));
      return;
    }
    
    if (e.key === 'Backspace' && !labelInput && selectedLabels.length > 0) {
      // Remove last label when backspace on empty input
      setSelectedLabels(prev => prev.slice(0, -1));
      return;
    }
    
    if (e.key === 'Tab' && labelSuggestions.length > 0) {
      e.preventDefault();
      addLabel(labelSuggestions[highlightedSuggestion]);
      return;
    }
  };
  
  /**
   * Add a label to selection
   */
  const addLabel = (label: string) => {
    if (!label.trim()) return;
    const normalized = label.trim();
    if (!selectedLabels.includes(normalized)) {
      setSelectedLabels(prev => [...prev, normalized]);
    }
    setLabelInput('');
    setHighlightedSuggestion(0);
    labelsRef.current?.focus();
  };
  
  /**
   * Remove a label from selection
   */
  const removeLabel = (label: string) => {
    setSelectedLabels(prev => prev.filter(l => l !== label));
    labelsRef.current?.focus();
  };

  /**
   * Start editing a field (if not completed)
   */
  const startEdit = (field: EditableField) => {
    if (!task.isCompleted) {
      setEditingField(field);
    }
  };

  const parsedDue = task.due?.date ? parseISO(task.due.date) : null;
  const dueDate = parsedDue && isValid(parsedDue) ? parsedDue : null;
  const isOverdue = dueDate && isPast(dueDate) && !isToday(dueDate);
  const isDueToday = dueDate && isToday(dueDate);

  return (
    <div className="h-full flex flex-col bg-surface-raised-light dark:bg-surface-base-dark relative">
      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-16 space-y-6">
        {/* Parent Task Link */}
        {task.parentId && onSelectTask && (
          <button
            onClick={() => onSelectTask(task.parentId!)}
            className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors cursor-pointer group"
          >
            <ArrowUpCircle className="w-4 h-4" />
            <span className="group-hover:underline">Go to parent task</span>
          </button>
        )}
        
        {/* Task Title & Completion with Close Button */}
        <div className="flex items-start gap-3">
          <button
            onClick={() => {
              if (task.isCompleted && !isPendingCompletion) {
                onReopen?.(task.id);
              } else if (!task.isCompleted && !isPendingCompletion) {
                setShowTimeInput(!showTimeInput);
              }
            }}
            disabled={isCompleting || isPendingCompletion}
            className={clsx(
              'shrink-0 w-7 h-7 md:w-6 md:h-6 rounded-sm border-2 flex items-center justify-center transition-all group mt-1.5',
              (task.isCompleted || isPendingCompletion)
                ? 'bg-primary-500 border-primary-500 text-surface-base-dark cursor-pointer hover:bg-primary-400 hover:border-primary-400'
                : clsx(priorityBorderColors[editPriority], 'cursor-pointer')
            )}
            aria-label={task.isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
          >
            {(task.isCompleted || isPendingCompletion) ? (
              <Check className="w-4 h-4" />
            ) : (
              <Check 
                className={clsx(
                  'w-3 h-3 opacity-0 group-hover:opacity-70 transition-opacity duration-75',
                  priorityCheckColors[editPriority]
                )}
                strokeWidth={3}
              />
            )}
          </button>
          <div className="flex-1 min-w-0">
            {editingField === 'title' ? (
              <textarea
                ref={titleRef}
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onBlur={() => saveField('title')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    saveField('title');
                  } else if (e.key === 'Escape') {
                    cancelEdit();
                  }
                }}
                disabled={savingField === 'title'}
                className="w-full text-lg font-medium text-text-primary-light dark:text-text-primary-dark bg-surface-raised-light dark:bg-surface-raised-dark px-2 py-1 -mx-2 border border-primary-300 dark:border-primary-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none overflow-hidden"
                rows={1}
              />
            ) : (
              <h3
                onClick={() => startEdit('title')}
                className={clsx(
                  'text-lg font-medium group break-words',
                  // Show strikethrough only if truly completed (not in pending/undo state)
                  task.isCompleted && !isPendingCompletion
                    ? 'text-text-tertiary-light dark:text-text-tertiary-dark line-through cursor-default'
                    : 'text-text-primary-light dark:text-text-primary-dark cursor-pointer hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded px-2 py-1 -mx-2 transition-colors'
                )}
              >
                {task.content}
                {!task.isCompleted && (
                  <Pencil className="w-3.5 h-3.5 inline-block ml-2 opacity-0 group-hover:opacity-50 transition-opacity text-text-tertiary-light dark:text-text-tertiary-dark" />
                )}
              </h3>
            )}
          </div>
          <button
            onClick={onClose}
            className="shrink-0 p-2.5 md:p-1.5 rounded-lg hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-secondary-light dark:hover:text-text-secondary-dark cursor-pointer mt-0.5"
            aria-label="Close"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Action Buttons - subtle row below title */}
        {task.isCompleted && !isPendingCompletion && (
          <div className="flex items-center gap-1 -mt-[25px] mb-5 ml-[30px]">
            <button
              onClick={() => onReopen?.(task.id)}
              className="flex items-center gap-1.5 px-2 py-0.5 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded transition-colors cursor-pointer"
              title="Mark as incomplete"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span className="font-medium">Mark as incomplete</span>
            </button>
            {task.completedAt && (
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-mono">
                <CheckCircle2 className="w-3 h-3" />
                <span>Completed {formatCompletedDate(task.completedAt)}</span>
              </div>
            )}
          </div>
        )}
        {!task.isCompleted && !isPendingCompletion && (
          <div className="flex items-center gap-2 -mt-[25px] mb-5 ml-[30px]">
            {/* Active snooze badge */}
            {task.snoozeUntil && (
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-mono">
                <Clock className="w-3 h-3" />
                <span>Wakes up {formatSnoozeDisplay(task.snoozeUntil)}</span>
                <button
                  onClick={handleClearSnooze}
                  disabled={isSnoozing}
                  className="ml-0.5 hover:text-amber-900 dark:hover:text-amber-200 transition-colors"
                  aria-label="Clear snooze"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            {/* Google Calendar badge */}
            {task.gcalEventId && (
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-mono">
                <CalendarDays className="w-3 h-3" />
                <span>On calendar</span>
              </div>
            )}

            {/* Energy level badge */}
            {task.metadata?.energyLevel && (
              <div className={clsx(
                'flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono',
                task.metadata.energyLevel === 'high' && 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
                task.metadata.energyLevel === 'medium' && 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
                task.metadata.energyLevel === 'low' && 'bg-surface-secondary-light dark:bg-surface-secondary-dark text-text-tertiary-light dark:text-text-tertiary-dark',
              )}>
                <Zap className="w-3 h-3" />
                <span>{task.metadata.energyLevel.charAt(0).toUpperCase() + task.metadata.energyLevel.slice(1)} energy</span>
              </div>
            )}

            {/* Snooze popover trigger — hidden when already snoozed */}
            {!task.snoozeUntil && (
              <Popover.Root open={showSnoozePopover} onOpenChange={setShowSnoozePopover}>
                <Popover.Trigger asChild>
                  <button
                    className="p-1.5 rounded transition-colors text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-secondary-light dark:hover:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark"
                    aria-label="Snooze task"
                    title="Snooze task"
                  >
                    <Clock className="w-4 h-4" />
                  </button>
                </Popover.Trigger>

                <Popover.Portal>
                  <Popover.Content
                    align="start"
                    sideOffset={4}
                    className="z-50 w-52 rounded-lg border border-border-subtle-light dark:border-border-subtle-dark bg-surface-raised-light dark:bg-surface-raised-dark shadow-xl p-2 space-y-1"
                  >
                    <p className="text-xs md:text-[10px] font-mono font-semibold uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary-dark px-2 pb-1">
                      Snooze until...
                    </p>

                    {SNOOZE_OPTIONS.map((opt) => (
                      <button
                        key={opt.label}
                        onClick={() => handleSnooze(opt.getValue())}
                        disabled={isSnoozing}
                        className="w-full text-left px-2 py-1.5 rounded text-sm font-mono text-text-primary-light dark:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors disabled:opacity-50"
                      >
                        {opt.label}
                      </button>
                    ))}

                    <div className="border-t border-border-subtle-light dark:border-border-subtle-dark pt-1 mt-1">
                      {showSnoozeDatePicker ? (
                        <DatePicker
                          value={snoozePickerDate}
                          onChange={handleSnoozeDatePick}
                        />
                      ) : (
                        <button
                          onClick={() => setShowSnoozeDatePicker(true)}
                          className="w-full text-left px-2 py-1.5 rounded text-sm font-mono text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors"
                        >
                          Pick a date...
                        </button>
                      )}
                    </div>

                    {snoozeError && (
                      <p className="text-xs font-mono text-red-500 px-2 pb-1">{snoozeError}</p>
                    )}

                    <Popover.Arrow className="fill-surface-raised-light dark:fill-surface-raised-dark" />
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>
            )}

            <button
              onClick={handleDuplicate}
              disabled={isDuplicating}
              className="flex items-center gap-1.5 px-2 py-0.5 text-xs text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-secondary-light dark:hover:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              title="Duplicate task"
            >
              {isDuplicating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
              <span className="font-medium">Duplicate</span>
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex items-center gap-1.5 px-2 py-0.5 text-xs text-text-tertiary-light dark:text-text-tertiary-dark hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 rounded transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              title="Archive task"
            >
              {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
              <span className="text-sm md:text-[13px] font-medium">Archive</span>
            </button>
          </div>
        )}

        {/* Completion Time Input */}
        {showTimeInput && !task.isCompleted && (
          <div className="animate-fade-in bg-primary-50 dark:bg-primary-950 rounded-lg p-4 space-y-3">
            <p className="text-sm text-primary-700 dark:text-primary-300 font-medium">
              How long did this take?
            </p>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={completionMinutes}
                onChange={(e) => setCompletionMinutes(e.target.value)}
                placeholder="Minutes"
                className="flex-1 px-3 py-2 border border-primary-200 dark:border-primary-700 bg-surface-raised-light dark:bg-surface-raised-dark text-text-primary-light dark:text-text-primary-dark rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                onClick={handleComplete}
                disabled={isCompleting}
                className="px-4 py-2 bg-primary-700 text-white rounded-lg font-medium text-sm hover:bg-primary-800 transition-colors disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
              >
                {isCompleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Complete'
                )}
              </button>
            </div>
            <button
              onClick={handleComplete}
              disabled={isCompleting}
              className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 cursor-pointer"
            >
              Skip time tracking
            </button>
          </div>
        )}

        {/* Description - Always visible textarea like other fields */}
        <div className="space-y-2.5">
          <div className="flex items-center justify-between">
            <h4 className="text-sm md:text-[13px] font-medium text-text-tertiary-light dark:text-text-tertiary-dark">
              Description
            </h4>
            {/* Copy button - only show when there's a description */}
            {task.description && (
              <button
                onClick={handleCopyDescription}
                className={clsx(
                  'p-2.5 md:p-1.5 rounded-md transition-colors cursor-pointer',
                  hasCopied
                    ? 'bg-green-50 dark:bg-green-950 text-green-600 dark:text-green-400'
                    : 'hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-secondary-light dark:hover:text-text-secondary-dark'
                )}
                title={hasCopied ? 'Copied!' : 'Copy description'}
                aria-label="Copy description to clipboard"
              >
                {hasCopied ? (
                  <ClipboardCheck className="w-3.5 h-3.5" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </button>
            )}
          </div>
          
          {/* Grid stack: both elements always in DOM in the same cell — no mount/unmount,
               no layout shift. The active element is visible; the inactive one is invisible
               but still occupies space so the cell height never changes on toggle.

               min-w-0 on the grid container AND the grid items is load-bearing: CSS
               Grid items default to min-width: auto (the longest unbreakable token).
               A long URL in the description would stretch the cell past the viewport,
               and break-all on the anchor couldn't help because the textarea/preview
               was already wider than the screen. min-w-0 lets the cell shrink so the
               wrap rules (overflow-wrap: anywhere on textarea, [&_a]:break-all on
               MarkdownContent) finally have something to bite against. */}
          <div className="grid min-w-0">
            {/* Preview (rendered markdown) — hidden while editing */}
            <div
              ref={(el) => {
                descriptionPreviewRef.current = el;
                if (el) descriptionPreviewHeightRef.current = el.offsetHeight;
              }}
              onClick={() => !task.isCompleted && startEdit('description')}
              style={{ gridArea: '1/1' }}
              className={clsx(
                'w-full min-w-0 text-sm rounded-lg pt-[11px] pb-[16px] px-[13px] transition-[background-color,border-color] min-h-[60px] overflow-hidden',
                'bg-surface-base-light dark:bg-surface-raised-dark',
                'border border-border-subtle-light dark:border-border-subtle-dark',
                'text-text-primary-light dark:text-text-primary-dark',
                task.isCompleted
                  ? 'cursor-not-allowed opacity-60'
                  : 'hover:border-border-hover-light dark:hover:border-border-hover-dark cursor-text',
                editingField === 'description' && 'invisible pointer-events-none'
              )}
            >
              {task.description ? (
                <div className="min-w-0" onClick={(e) => { if ((e.target as HTMLElement).tagName === 'A') e.stopPropagation(); }}>
                  <MarkdownContent content={task.description} />
                </div>
              ) : (
                <span className="text-text-tertiary-light dark:text-text-tertiary-dark">
                  Add a description...
                </span>
              )}
            </div>

            {/* Textarea (edit mode) — hidden while not editing */}
            <textarea
              ref={descriptionRef}
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              onBlur={() => saveField('description')}
              onKeyDown={(e) => handleKeyDown(e, 'description')}
              disabled={savingField === 'description' || task.isCompleted}
              placeholder="Add a description..."
              tabIndex={editingField === 'description' ? 0 : -1}
              aria-hidden={editingField !== 'description'}
              style={{ gridArea: '1/1' }}
              className={clsx(
                'w-full min-w-0 text-sm rounded-lg pt-[11px] pb-[16px] px-[13px] resize-none overflow-hidden transition-[background-color,border-color,box-shadow] min-h-[60px]',
                'bg-surface-base-light dark:bg-surface-raised-dark',
                'border border-border-subtle-light dark:border-border-subtle-dark',
                'text-text-primary-light dark:text-text-primary-dark',
                'placeholder-text-tertiary-light dark:placeholder-text-tertiary-dark',
                'hover:border-border-hover-light dark:hover:border-border-hover-dark focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                editingField !== 'description' && 'invisible pointer-events-none'
              )}
            />
          </div>
        </div>

        {/* Metadata grid — shared primitives keep create + edit in lockstep */}
        <TaskMetadataGrid>
          <FieldRow label="Due Date">
            <DatePicker
              value={editDueDate}
              onChange={(val) => {
                setEditDueDate(val);
                saveField('dueDate', val);
              }}
              timeValue={editDueTime}
              onTimeChange={(time) => {
                setEditDueTime(time);
                if (!task.isCompleted && editDueDate) {
                  setSavingField('dueDate');
                  setSaveError(null);
                  updateTaskAsync({
                    taskId: task.id,
                    updates: { dueDate: editDueDate, dueTime: time },
                  })
                    .then((updated) => onTaskUpdate?.(updated))
                    .catch((err: any) => setSaveError(err.message || 'Failed to update time'))
                    .finally(() => setSavingField(null));
                }
              }}
              disabled={savingField === 'dueDate' || task.isCompleted}
              variant={isOverdue ? 'overdue' : isDueToday ? 'today' : 'default'}
            />
          </FieldRow>

          <FieldRow label="Priority">
            <FieldSelect
              ref={priorityRef}
              value={priorityValueForSelect(editPriority)}
              onChange={(e) => {
                const newPriority = Number(e.target.value);
                setEditPriority(newPriority);
                saveField('priority', newPriority);
              }}
              onKeyDown={(e) => handleKeyDown(e, 'priority')}
              disabled={savingField === 'priority' || task.isCompleted}
              className={priorityColors[priorityValueForSelect(editPriority)]}
            >
              {priorityOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </FieldSelect>
          </FieldRow>

          <FieldRow label="Project">
            {showNewProject ? (
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  autoFocus
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); void handleCreateProject(); }
                    else if (e.key === 'Escape') { e.preventDefault(); setShowNewProject(false); setNewProjectName(''); }
                  }}
                  placeholder="New project name"
                  className={inputClasses}
                />
                <button
                  type="button"
                  onClick={() => void handleCreateProject()}
                  disabled={!newProjectName.trim() || isCreatingProject}
                  className={clsx(btn.primary.sm, 'shrink-0')}
                >
                  {isCreatingProject ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Add'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowNewProject(false); setNewProjectName(''); }}
                  className="shrink-0 p-2 rounded-lg text-text-tertiary-light dark:text-text-tertiary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark cursor-pointer"
                  aria-label="Cancel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <FieldSelect
                ref={projectRef}
                value={editProject}
                onChange={(e) => handleProjectChange(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, 'project')}
                disabled={savingField === 'project' || task.isCompleted}
              >
                <option value="">No Project</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
                <option value={NEW_PROJECT_VALUE}>+ Create new project…</option>
              </FieldSelect>
            )}
          </FieldRow>

          <FieldRow label="Estimate">
            <FieldSelect
              ref={timeEstimateRef}
              value={editTimeEstimate}
              onChange={(e) => {
                setEditTimeEstimate(e.target.value);
                saveField('timeEstimate', e.target.value);
              }}
              onKeyDown={(e) => handleKeyDown(e, 'timeEstimate')}
              disabled={savingField === 'timeEstimate' || task.isCompleted}
            >
              <option value="">No estimate</option>
              {TIME_ESTIMATE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </FieldSelect>
          </FieldRow>

          {/* Repeat spans 2 cols to avoid a widow row */}
          <FieldRow label="Repeat" full>
            <FieldSelect
              value={editRecurrenceRule}
              onChange={(e) => {
                const newRule = e.target.value;
                setEditRecurrenceRule(newRule);
                saveField('recurrenceRule', newRule);
              }}
              disabled={savingField === 'recurrenceRule' || task.isCompleted}
            >
              <option value="">Does not repeat</option>
              {RECURRENCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </FieldSelect>
          </FieldRow>
        </TaskMetadataGrid>

        {/* Labels - Full width with suggestions */}
        <div className="space-y-2.5">
          <h4 className="text-sm md:text-[13px] font-medium text-text-tertiary-light dark:text-text-tertiary-dark">
            Labels
          </h4>
          {editingField === 'labels' ? (
            <div className="relative">
              {/* Selected labels + input */}
              <div
                data-labels-container
                className="flex flex-wrap gap-1.5 p-2 bg-surface-raised-light dark:bg-surface-raised-dark border border-primary-300 dark:border-primary-600 rounded-lg focus-within:ring-2 focus-within:ring-primary-500 min-h-[44px]"
                onClick={() => labelsRef.current?.focus()}
              >
                {selectedLabels.map((label) => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded"
                  >
                    {label}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeLabel(label);
                      }}
                      className="hover:text-primary-900 dark:hover:text-primary-100 cursor-pointer"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <input
                  ref={labelsRef}
                  type="text"
                  value={labelInput}
                  onChange={(e) => {
                    setLabelInput(e.target.value);
                    setHighlightedSuggestion(0);
                    setShowLabelSuggestions(true);
                  }}
                  onKeyDown={handleLabelKeyDown}
                  onFocus={() => setShowLabelSuggestions(true)}
                  disabled={savingField === 'labels'}
                  placeholder={selectedLabels.length === 0 ? "Type to search or add labels..." : ""}
                  className="flex-1 min-w-[120px] px-1 py-0.5 text-sm outline-none bg-transparent text-text-primary-light dark:text-text-primary-dark placeholder-text-tertiary-light dark:placeholder-text-tertiary-dark"
                />
              </div>

              {/* Suggestions dropdown */}
              {showLabelSuggestions && (labelSuggestions.length > 0 || canAddNewLabel) && (
                <div
                  ref={suggestionsRef}
                  className="absolute z-10 w-full mt-1 bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg shadow-lg max-h-48 overflow-y-auto"
                >
                  {labelSuggestions.map((label, index) => (
                    <button
                      key={label}
                      onClick={() => addLabel(label)}
                      className={clsx(
                        'w-full px-3 py-2 text-left text-sm flex items-center gap-2 cursor-pointer',
                        index === highlightedSuggestion
                          ? 'bg-primary-50 dark:bg-primary-950 text-primary-700 dark:text-primary-300'
                          : 'hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark text-text-primary-light dark:text-text-primary-dark'
                      )}
                    >
                      <Tag className="w-3.5 h-3.5 text-text-tertiary-light" />
                      {label}
                    </button>
                  ))}
                  {canAddNewLabel && (
                    <button
                      onClick={() => addLabel(labelInput.trim())}
                      className={clsx(
                        'w-full px-3 py-2 text-left text-sm flex items-center gap-2 cursor-pointer border-t border-border-subtle-light dark:border-border-subtle-dark',
                        highlightedSuggestion === labelSuggestions.length
                          ? 'bg-primary-50 dark:bg-primary-950 text-primary-700 dark:text-primary-300'
                          : 'hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark text-text-primary-light dark:text-text-primary-dark'
                      )}
                    >
                      <Plus className="w-3.5 h-3.5 text-primary-500" />
                      Create "{labelInput.trim()}"
                    </button>
                  )}
                </div>
              )}

              {/* Action buttons */}
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-text-tertiary-light">Enter to add, Backspace to remove</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={cancelEdit}
                    disabled={savingField === 'labels'}
                    className="px-3 py-1.5 text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => saveField('labels')}
                    disabled={savingField === 'labels'}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-primary-700 hover:bg-primary-800 rounded transition-colors cursor-pointer flex items-center gap-1"
                  >
                    {savingField === 'labels' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            (() => {
              // Filter out time estimate labels for display
              const displayLabels = filterTimeLabels(task.labels || []);
              return (
                <div
                  onClick={() => startEdit('labels')}
                  className={clsx(
                    'flex flex-wrap gap-1.5 p-[13px] bg-surface-base-light dark:bg-surface-raised-dark rounded-lg min-h-[44px] max-h-[120px] overflow-y-auto group border border-transparent',
                    task.isCompleted
                      ? 'cursor-default'
                      : 'cursor-pointer hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark hover:border-border-hover-light dark:hover:border-border-hover-dark transition-colors'
                  )}
                >
                  {displayLabels.length > 0 ? (
                    <>
                      {displayLabels.map((label) => (
                        <span
                          key={label}
                          className="px-2 py-0.5 text-xs font-medium bg-surface-hover-light dark:bg-surface-hover-dark text-text-primary-light dark:text-text-primary-dark rounded"
                        >
                          {label}
                        </span>
                      ))}
                      {!task.isCompleted && (
                        <Pencil className="w-3.5 h-3.5 ml-auto opacity-0 group-hover:opacity-50 transition-opacity text-text-tertiary-light dark:text-text-tertiary-dark self-center" />
                      )}
                    </>
                  ) : (
                    <span className="text-sm text-text-tertiary-light dark:text-text-tertiary-dark">
                      {task.isCompleted ? 'No labels' : 'Click to add labels...'}
                    </span>
                  )}
                </div>
              );
            })()
          )}
        </div>

        {/* Comments Section */}
        <TaskComments taskId={task.id} isCompleted={task.isCompleted} />

        {/* Subtasks Section */}
        <SubtasksList taskId={task.id} isCompleted={task.isCompleted} onSelectTask={onSelectTask} />

        {/* Timer Section */}
        <TaskTimerSection taskId={task.id} isCompleted={task.isCompleted} />

        {/* Supplies */}
        {task.metadata?.supplies && task.metadata.supplies.length > 0 && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark">
              <Package className="w-3.5 h-3.5" />
              <span>Supplies needed</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {task.metadata.supplies.map((item) => (
                <span
                  key={item}
                  className="px-2 py-0.5 rounded-full text-xs font-mono bg-surface-secondary-light dark:bg-surface-secondary-dark text-text-secondary-light dark:text-text-secondary-dark border border-border-light dark:border-border-dark"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Save Error */}
        {saveError && (
          <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {saveError}
          </div>
        )}

        {/* AI Confidence */}
        {task.metadata?.aiConfidence !== undefined && task.metadata.aiConfidence > 0 && (
          <div className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark flex items-center gap-1">
            <span>AI classification confidence: {Math.round(task.metadata.aiConfidence * 100)}%</span>
          </div>
        )}
      </div>

      {/* Floating action button */}
      {task.isCompleted && !isPendingCompletion && (
        <button
          onClick={() => onReopen?.(task.id)}
          className="absolute bottom-4 right-4 flex items-center gap-1.5 px-4 py-2.5 md:px-3 md:py-1.5 bg-primary-700 hover:bg-primary-600 text-white rounded-lg font-mono text-sm md:text-xs font-medium shadow-lg transition-colors cursor-pointer"
        >
          <RotateCcw className="w-4 h-4 md:w-3.5 md:h-3.5" strokeWidth={2.5} />
          Reopen
        </button>
      )}
      {!task.isCompleted && !showTimeInput && (
        <button
          onClick={() => setShowTimeInput(true)}
          className="absolute bottom-4 right-4 flex items-center gap-1.5 px-4 py-2.5 md:px-3 md:py-1.5 bg-primary-700 hover:bg-primary-600 text-white rounded-lg font-mono text-sm md:text-xs font-medium shadow-lg transition-colors cursor-pointer"
        >
          <Check className="w-4 h-4 md:w-3.5 md:h-3.5" strokeWidth={2.5} />
          Mark Complete
        </button>
      )}
    </div>
  );
}
